/**
 * Pure, UI-independent Rang match logic.
 *
 * Nothing in this file touches the DOM, storage, or the network, so the same
 * code runs in the browser (optimistic updates), in the Worker (authoritative
 * validation), in tests, and later in a playable online game.
 *
 * Seats are numbers 0..3 in clockwise order viewed from above:
 *   0 = North, 1 = East, 2 = South, 3 = West.
 * Partners sit opposite: Team A = N + S, Team B = E + W.
 */

export const SEAT_NAMES = ['North', 'East', 'South', 'West'] as const;
export const SEAT_SHORT = ['N', 'E', 'S', 'W'] as const;
export type Seat = 0 | 1 | 2 | 3;
export const ALL_SEATS: readonly Seat[] = [0, 1, 2, 3];

export type TeamId = 'A' | 'B';
export const TEAMS: readonly TeamId[] = ['A', 'B'];

export const SUITS = ['S', 'H', 'D', 'C'] as const;
export type Suit = (typeof SUITS)[number];
export const SUIT_SYMBOL: Record<Suit, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
export const SUIT_NAME: Record<Suit, string> = { S: 'Spades', H: 'Hearts', D: 'Diamonds', C: 'Clubs' };

export const TRICKS_PER_DEAL = 13;
/** Tricks the team that chose Rang needs to be saved (a majority of 13). */
export const TRICKS_TO_SAVE = 7;
export const PLAYER_COUNT = 4;

// ---------------------------------------------------------------------------
// House rules
// ---------------------------------------------------------------------------

export type TurnDirection = 'anticlockwise' | 'clockwise';
export type TrumpCaller = 'afterDealer' | 'dealer' | 'highestBidder';
export type CourtMode = 'all13' | 'first7InRow';

export interface CourtRules {
  enabled: boolean;
  /** Extra Guaa on top of `guaaPerLoss` when the Rang team is beaten by a court. */
  bonus: number;
  /** What counts as a court at this table: losing all 13 tricks, or losing the first 7 in a row. */
  mode: CourtMode;
}

/**
 * Scoring is always Guaa: in every deal only the team that chose Rang is at risk.
 * If it loses the deal it gets Guaa; if it wins it is "saved" (0). Fewest Guaa wins.
 */
export interface HouseRules {
  turnDirection: TurnDirection;
  dealsPerPlayer: number;
  /** Consecutive deals a dealer keeps the deck for before it passes on (divides `dealsPerPlayer`). */
  dealsPerTurn: number;
  /**
   * Rounds added after the match was extended ("keep playing"): in each, every
   * player deals this many hands in a row. Empty for a regular match.
   */
  extraRounds: number[];
  /** Guaa given to the Rang team when it loses a deal. */
  guaaPerLoss: number;
  court: CourtRules;
  trumpCaller: TrumpCaller;
  endEarlyWhenDecided: boolean;
}

export const RULE_LIMITS = {
  dealsPerPlayer: { min: 1, max: 6 },
  dealsPerTurn: { min: 1, max: 6 },
  extraRounds: { min: 1, max: 6, maxRounds: 10 },
  guaaPerLoss: { min: 1, max: 5 },
  courtBonus: { min: 0, max: 10 },
} as const;

export const DEFAULT_RULES: HouseRules = {
  turnDirection: 'anticlockwise',
  dealsPerPlayer: 3,
  dealsPerTurn: 3,
  extraRounds: [],
  guaaPerLoss: 1,
  court: { enabled: true, bonus: 2, mode: 'all13' },
  trumpCaller: 'afterDealer',
  endEarlyWhenDecided: false,
};

export interface RulePreset {
  id: string;
  /** Default display name; users can rename presets locally. */
  defaultName: string;
  description: string;
  rules: HouseRules;
}

export const PRESETS: readonly RulePreset[] = [
  {
    id: 'standard',
    defaultName: 'Standard',
    description:
      'Dealer deals 3 in a row to the next player, who calls Rang. If that team loses the deal it gets a Guaa; Court = +2 more. Fewest Guaa wins.',
    rules: DEFAULT_RULES,
  },
  {
    id: 'regional-1',
    defaultName: 'Regional style 1',
    description: 'The deck passes after every hand instead of every 3; the dealer calls Rang.',
    rules: {
      ...DEFAULT_RULES,
      dealsPerTurn: 1,
      trumpCaller: 'dealer',
    },
  },
  {
    id: 'regional-2',
    defaultName: 'Regional style 2',
    description: 'Court for losing the first 7 tricks in a row (+3 Guaa); the match ends once decided.',
    rules: {
      ...DEFAULT_RULES,
      court: { enabled: true, bonus: 3, mode: 'first7InRow' },
      endEarlyWhenDecided: true,
    },
  },
];

export function rulesEqual(a: HouseRules, b: HouseRules): boolean {
  return JSON.stringify(normalizeRules(a)) === JSON.stringify(normalizeRules(b));
}

/** Returns a copy with keys in canonical order (stable for comparisons and storage). */
export function normalizeRules(r: HouseRules): HouseRules {
  return {
    turnDirection: r.turnDirection,
    dealsPerPlayer: r.dealsPerPlayer,
    dealsPerTurn: r.dealsPerTurn,
    extraRounds: [...(r.extraRounds ?? [])],
    guaaPerLoss: r.guaaPerLoss,
    court: { enabled: r.court.enabled, bonus: r.court.bonus, mode: r.court.mode },
    trumpCaller: r.trumpCaller,
    endEarlyWhenDecided: r.endEarlyWhenDecided,
  };
}

/** Rules JSON as saved by earlier versions (points scoring, `pointsPerDeal`, no `dealsPerTurn`). */
export type StoredRules = Omit<HouseRules, 'guaaPerLoss' | 'dealsPerTurn' | 'extraRounds'> &
  Partial<Pick<HouseRules, 'guaaPerLoss' | 'dealsPerTurn' | 'extraRounds'>> & { pointsPerDeal?: number; scoring?: string; trickTarget?: number };

/** True for rules saved before Guaa scoring; their deals must be re-scored. */
export function isLegacyRules(raw: StoredRules): boolean {
  return raw.guaaPerLoss === undefined;
}

/**
 * Converts rules saved by earlier versions. Matches that were scored with points
 * move to Guaa, and to 3 hands per dealer where the deal count allows it.
 */
export function upgradeRules(raw: StoredRules): HouseRules {
  if (!isLegacyRules(raw)) return normalizeRules(raw as HouseRules);
  const keepTurn = raw.scoring === 'guaa' && raw.dealsPerTurn !== undefined;
  const dealsPerTurn = keepTurn
    ? raw.dealsPerTurn!
    : Math.max(...dealsPerTurnOptions(raw.dealsPerPlayer).filter((n) => n <= DEFAULT_RULES.dealsPerTurn));
  return normalizeRules({ ...raw, guaaPerLoss: raw.pointsPerDeal ?? 1, dealsPerTurn, extraRounds: raw.extraRounds ?? [] });
}

/** Cross-field checks for a rules object, or `null` when it's consistent. */
export function rulesProblem(r: HouseRules): string | null {
  if (r.dealsPerPlayer % r.dealsPerTurn !== 0) return 'Deals per turn must divide deals per player evenly.';
  if (r.extraRounds.length > RULE_LIMITS.extraRounds.maxRounds) return 'Too many extra rounds.';
  return null;
}

/** Valid "deals per dealer turn" choices for a given number of deals per player. */
export function dealsPerTurnOptions(dealsPerPlayer: number): number[] {
  return Array.from({ length: dealsPerPlayer }, (_, i) => i + 1).filter((n) => dealsPerPlayer % n === 0);
}

// ---------------------------------------------------------------------------
// Seats, teams, rotation
// ---------------------------------------------------------------------------

export function teamOfSeat(seat: Seat): TeamId {
  return seat % 2 === 0 ? 'A' : 'B';
}

export function seatsOfTeam(team: TeamId): [Seat, Seat] {
  return team === 'A' ? [0, 2] : [1, 3];
}

export function otherTeam(team: TeamId): TeamId {
  return team === 'A' ? 'B' : 'A';
}

export function partnerOf(seat: Seat): Seat {
  return ((seat + 2) % 4) as Seat;
}

export function isSeat(n: unknown): n is Seat {
  return n === 0 || n === 1 || n === 2 || n === 3;
}

/**
 * The seat that acts after `seat`. Viewed from above with North at the top,
 * clockwise is N → E → S → W and anticlockwise is N → W → S → E.
 */
export function nextSeat(seat: Seat, direction: TurnDirection, steps = 1): Seat {
  const delta = direction === 'clockwise' ? 1 : 3;
  return (((seat + delta * steps) % 4) + 4) % 4 as Seat;
}

/**
 * Hands in each dealer turn, in order: the regular rounds (`dealsPerTurn` each),
 * then 4 turns of `k` hands for every extra round.
 */
export function turnLengths(rules: HouseRules): number[] {
  const regular = (rules.dealsPerPlayer / rules.dealsPerTurn) * PLAYER_COUNT;
  const out: number[] = Array(regular).fill(rules.dealsPerTurn);
  for (const k of rules.extraRounds) out.push(k, k, k, k);
  return out;
}

export function scheduledDealCount(rules: HouseRules): number {
  return turnLengths(rules).reduce((a, b) => a + b, 0);
}

/** Hands each player deals over the whole (possibly extended) match. */
export function handsPerPlayer(rules: HouseRules): number {
  return rules.dealsPerPlayer + rules.extraRounds.reduce((a, b) => a + b, 0);
}

/**
 * Locates a 1-based deal within the dealer turns. Deals past the schedule
 * (sudden death) continue in turns as long as the last one.
 */
function locateDeal(dealNo: number, rules: HouseRules): { turn: number; dealInTurn: number; turnLength: number } {
  const lengths = turnLengths(rules);
  let n = dealNo;
  for (let turn = 0; turn < lengths.length; turn++) {
    if (n <= lengths[turn]!) return { turn, dealInTurn: n, turnLength: lengths[turn]! };
    n -= lengths[turn]!;
  }
  const last = lengths.at(-1) ?? rules.dealsPerTurn;
  const extra = Math.floor((n - 1) / last);
  return { turn: lengths.length + extra, dealInTurn: ((n - 1) % last) + 1, turnLength: last };
}

/** Which dealer turn (0-based) a 1-based deal number falls in. */
export function turnOfDeal(dealNo: number, rules: HouseRules): number {
  return locateDeal(dealNo, rules).turn;
}

/** Rules for continuing a match with one more round of `hands` per player. */
export function withExtraRound(rules: HouseRules, hands: number): HouseRules {
  return { ...rules, extraRounds: [...rules.extraRounds, hands] };
}

/** Keep playing: one more round of `hands` each; reopens a finished match. */
export function continueMatch(state: MatchState, hands: number): MatchState {
  if (hands < RULE_LIMITS.extraRounds.min || hands > RULE_LIMITS.extraRounds.max) throw new RuleError('Choose 1–6 hands.');
  if (state.rules.extraRounds.length >= RULE_LIMITS.extraRounds.maxRounds) throw new RuleError('This match has reached the round limit.');
  // Only after the scheduled hands are done (or once finished) — never mid-round.
  const played = state.deals.length;
  const rules = played < scheduledDealCount(state.rules) ? state.rules : withExtraRound(fitRulesToDeals(state.rules, played), hands);
  return { ...state, rules, closed: false };
}

/**
 * Dealer for each deal, starting from `firstDealer`. The dealer keeps the deck for
 * `dealsPerTurn` consecutive deals, then it passes one seat in the turn direction.
 */
export function dealerSchedule(firstDealer: Seat, rules: HouseRules, count = scheduledDealCount(rules)): Seat[] {
  return Array.from({ length: count }, (_, i) => nextSeat(firstDealer, rules.turnDirection, turnOfDeal(i + 1, rules)));
}

/** Who chooses Rang for a deal, or `null` when it is decided by bidding at the table. */
export function trumpCallerSeat(dealer: Seat, rules: HouseRules): Seat | null {
  switch (rules.trumpCaller) {
    case 'dealer':
      return dealer;
    case 'afterDealer':
      return nextSeat(dealer, rules.turnDirection);
    case 'highestBidder':
      return null;
  }
}

// ---------------------------------------------------------------------------
// Deals
// ---------------------------------------------------------------------------

export type DealResult = 'saved' | 'guaa';

/** What the scorer records after each deal. */
export interface DealInput {
  /** Did the team that chose Rang save itself (0) or get Guaa? */
  result: DealResult;
  /** The Rang team lost by a court (only with Guaa, when Court is enabled). */
  court?: boolean;
  trumpSuit: Suit | null;
  /** Team that chose Rang. Normally derived from the caller; required when Rang goes to the highest bidder. */
  trumpTeam?: TeamId | null;
}

export interface DealOutcome {
  isCourt: boolean;
  guaaA: number;
  guaaB: number;
}

export interface DealRecord extends DealOutcome {
  dealNo: number;
  dealerSeat: Seat;
  result: DealResult;
  trumpSuit: Suit | null;
  /** `null` only for deals recorded by old versions where it couldn't be known. */
  trumpTeam: TeamId | null;
  /** Trick counts recorded by older versions; new deals leave them `null`. */
  teamATricks?: number | null;
  teamBTricks?: number | null;
}

export class RuleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RuleError';
  }
}

/** Returns a human-readable problem with the input, or `null` when it is valid. */
export function validateDealInput(input: DealInput, rules: HouseRules): string | null {
  if (input.result !== 'saved' && input.result !== 'guaa') return 'Choose Saved or Guaa.';
  if (input.trumpSuit !== null && !SUITS.includes(input.trumpSuit)) return 'Unknown trump suit.';
  if (input.trumpTeam !== 'A' && input.trumpTeam !== 'B') return 'Choose which team called Rang.';
  if (input.court && !rules.court.enabled) return 'Court is turned off in the house rules.';
  if (input.court && input.result !== 'guaa') return 'A court only counts when the Rang team gets Guaa.';
  return null;
}

/** Works out the Guaa for one deal. Throws `RuleError` for invalid input. */
export function resolveDeal(input: DealInput, rules: HouseRules): DealOutcome {
  const problem = validateDealInput(input, rules);
  if (problem) throw new RuleError(problem);
  // Only the Rang team can get Guaa, and only when it loses; otherwise it is saved (0).
  const isCourt = input.result === 'guaa' && Boolean(input.court);
  const guaa = input.result === 'guaa' ? rules.guaaPerLoss + (isCourt ? rules.court.bonus : 0) : 0;
  return {
    isCourt,
    guaaA: input.trumpTeam === 'A' ? guaa : 0,
    guaaB: input.trumpTeam === 'B' ? guaa : 0,
  };
}

/** The most Guaa a single deal can give (used to decide a match early). */
export function maxGuaaPerDeal(rules: HouseRules): number {
  return rules.guaaPerLoss + (rules.court.enabled ? rules.court.bonus : 0);
}

// ---------------------------------------------------------------------------
// Match state
// ---------------------------------------------------------------------------

export interface MatchState {
  rules: HouseRules;
  /** Chosen by the magic wheel; `null` until spun. */
  firstDealer: Seat | null;
  deals: readonly DealRecord[];
  /** The players pressed "Finish match". Matches never end on their own otherwise. */
  closed?: boolean;
}

export interface Totals {
  guaaA: number;
  guaaB: number;
  /** Deals in which the team chose Rang. */
  calledA: number;
  calledB: number;
  /** Of those, deals it saved itself. */
  savedA: number;
  savedB: number;
  /** Courts the team suffered. */
  courtsA: number;
  courtsB: number;
}

/** Score is always derived from the deals; there are no stored running totals. */
export function totals(deals: readonly DealRecord[]): Totals {
  const t: Totals = { guaaA: 0, guaaB: 0, calledA: 0, calledB: 0, savedA: 0, savedB: 0, courtsA: 0, courtsB: 0 };
  for (const d of deals) {
    t.guaaA += d.guaaA;
    t.guaaB += d.guaaB;
    if (d.trumpTeam === 'A' || d.trumpTeam === 'B') {
      const k = d.trumpTeam;
      t[`called${k}`]++;
      if (d.result === 'saved') t[`saved${k}`]++;
      if (d.isCourt) t[`courts${k}`]++;
    }
  }
  return t;
}

/**
 * `roundComplete`: every scheduled hand has been dealt; the players choose to
 * keep playing (add a round) or finish. A match only ends when they finish it
 * (or, with `endEarlyWhenDecided`, once the trailing team can't catch up).
 */
export type MatchPhase = 'awaitingFirstDealer' | 'inProgress' | 'roundComplete' | 'finished';
export type FinishReason = 'closed' | 'decidedEarly';

export interface MatchStatus {
  phase: MatchPhase;
  /** Set when finished: the team with fewer Guaa, or `'tie'`. */
  result: TeamId | 'tie' | null;
  reason: FinishReason | null;
  /** Team currently ahead (fewer Guaa), or `null` when level. */
  leader: TeamId | null;
  /** 1-based number of the next deal to record. */
  nextDealNo: number;
  scheduledDeals: number;
}

export function matchStatus(state: MatchState): MatchStatus {
  const scheduledDeals = scheduledDealCount(state.rules);
  const played = state.deals.length;
  const t = totals(state.deals);
  const diff = t.guaaA - t.guaaB;
  // Fewer Guaa is better.
  const leader: TeamId | null = diff < 0 ? 'A' : diff > 0 ? 'B' : null;
  const base = { nextDealNo: played + 1, scheduledDeals, leader };
  if (state.firstDealer === null) return { ...base, phase: 'awaitingFirstDealer', result: null, reason: null };
  if (state.closed) return { ...base, phase: 'finished', result: leader ?? 'tie', reason: 'closed' };

  if (state.rules.endEarlyWhenDecided && leader && played < scheduledDeals) {
    // The gap can only close in deals where the leader calls Rang (or we can't know who will).
    let open = 0;
    for (let n = played + 1; n <= scheduledDeals; n++) {
      const team = trumpTeamForDeal(state, n);
      if (team === null || team === leader) open++;
    }
    // Strictly greater: if the trailer could still draw level the match isn't decided.
    if (Math.abs(diff) > open * maxGuaaPerDeal(state.rules)) {
      return { ...base, phase: 'finished', result: leader, reason: 'decidedEarly' };
    }
  }
  if (played >= scheduledDeals) return { ...base, phase: 'roundComplete', result: null, reason: null };
  return { ...base, phase: 'inProgress', result: null, reason: null };
}

/** Marks the match as finished by the players. */
export function finishMatch(state: MatchState): MatchState {
  if (state.deals.length === 0) throw new RuleError('Record at least one deal before finishing.');
  return { ...state, closed: true };
}

/**
 * Rules for older matches whose sudden-death deals went past the schedule: adds
 * extra rounds (same hands per turn as the last one) until every deal fits.
 */
export function fitRulesToDeals(rules: HouseRules, dealCount: number): HouseRules {
  let r = rules;
  while (scheduledDealCount(r) < dealCount && r.extraRounds.length < RULE_LIMITS.extraRounds.maxRounds) {
    r = withExtraRound(r, turnLengths(r).at(-1) ?? r.dealsPerTurn);
  }
  return r;
}

export function isFinished(state: MatchState): boolean {
  return matchStatus(state).phase === 'finished';
}

/**
 * Dealer for deal number `dealNo` (1-based). Completed deals keep the dealer
 * that was recorded; future deals rotate one seat from the last recorded dealer.
 */
export function dealerForDeal(state: MatchState, dealNo: number): Seat | null {
  if (state.firstDealer === null || dealNo < 1) return null;
  const recorded = state.deals[dealNo - 1];
  if (recorded) return recorded.dealerSeat;
  return nextSeat(state.firstDealer, state.rules.turnDirection, turnOfDeal(dealNo, state.rules));
}

/** Team that calls Rang in deal `dealNo`, or `null` when decided by bidding. */
export function trumpTeamForDeal(state: MatchState, dealNo: number): TeamId | null {
  const recorded = state.deals[dealNo - 1];
  if (recorded) return recorded.trumpTeam;
  const dealer = dealerForDeal(state, dealNo);
  const caller = dealer === null ? null : trumpCallerSeat(dealer, state.rules);
  return caller === null ? null : teamOfSeat(caller);
}

/** Position of a deal within its dealer's turn, e.g. deal 2 of 3. */
export function turnInfo(state: MatchState, dealNo: number) {
  const { turn, dealInTurn, turnLength } = locateDeal(dealNo, state.rules);
  return { turn: turn + 1, dealInTurn, dealsPerTurn: turnLength };
}

export function currentDealer(state: MatchState): Seat | null {
  if (matchStatus(state).phase !== 'inProgress') return null;
  return dealerForDeal(state, state.deals.length + 1);
}

export type ScheduleSlotState = 'done' | 'current' | 'upcoming';

export interface ScheduleSlot {
  dealNo: number;
  dealerSeat: Seat;
  state: ScheduleSlotState;
  deal?: DealRecord;
}

export function schedule(state: MatchState): ScheduleSlot[] {
  if (state.firstDealer === null) return [];
  const status = matchStatus(state);
  const count = Math.max(status.scheduledDeals, state.deals.length);
  const slots: ScheduleSlot[] = [];
  for (let n = 1; n <= count; n++) {
    const deal = state.deals[n - 1];
    const dealerSeat = dealerForDeal(state, n)!;
    let slotState: ScheduleSlotState = 'upcoming';
    if (deal) slotState = 'done';
    else if (n === status.nextDealNo && status.phase === 'inProgress') slotState = 'current';
    slots.push({ dealNo: n, dealerSeat, state: slotState, deal });
  }
  return slots;
}

/** How many deals each seat has dealt so far (sudden-death deals included). */
export function dealsDealtBySeat(state: MatchState): Record<Seat, number> {
  const counts: Record<Seat, number> = { 0: 0, 1: 0, 2: 0, 3: 0 };
  for (const d of state.deals) counts[d.dealerSeat]++;
  return counts;
}

/** Records the next deal. Throws `RuleError` when the match can't accept it. */
export function applyDeal(state: MatchState, input: DealInput): MatchState {
  const status = matchStatus(state);
  if (status.phase === 'awaitingFirstDealer') throw new RuleError('Spin the wheel to choose the first dealer first.');
  if (status.phase === 'finished') throw new RuleError('The match is already over.');
  if (status.phase === 'roundComplete') throw new RuleError('This round is complete. Keep playing or finish the match.');
  const dealNo = state.deals.length + 1;
  const trumpTeam = input.trumpTeam ?? trumpTeamForDeal(state, dealNo);
  const outcome = resolveDeal({ ...input, trumpTeam }, state.rules);
  const record: DealRecord = {
    dealNo,
    dealerSeat: dealerForDeal(state, dealNo)!,
    result: input.result,
    trumpSuit: input.trumpSuit,
    trumpTeam,
    teamATricks: null,
    teamBTricks: null,
    ...outcome,
  };
  return { ...state, deals: [...state.deals, record] };
}

/** Removes the last deal; a finished match is reopened. */
export function undoLastDeal(state: MatchState): MatchState {
  if (state.deals.length === 0) throw new RuleError('There is no deal to undo.');
  return { ...state, closed: false, deals: state.deals.slice(0, -1) };
}

/**
 * Re-scores every recorded deal under `rules` (used when house rules are changed
 * after play started). Recorded dealers are kept; a deal that is invalid under the
 * new rules raises `RuleError` so the change can be rejected.
 */
export function rescore(state: MatchState, rules: HouseRules): MatchState {
  const problem = rulesProblem(rules);
  if (problem) throw new RuleError(problem);
  const deals = state.deals.map((d) => {
    const caller = trumpCallerSeat(d.dealerSeat, rules);
    const trumpTeam = d.trumpTeam ?? (caller === null ? null : teamOfSeat(caller));
    // A court recorded earlier is dropped if Court has since been turned off.
    const court = d.isCourt && rules.court.enabled;
    const outcome = resolveDeal({ result: d.result, court, trumpSuit: d.trumpSuit, trumpTeam }, rules);
    return { ...d, trumpTeam, ...outcome };
  });
  const next: MatchState = { ...state, rules, deals };
  // Changing e.g. dealsPerPlayer could leave deals recorded after the match would have ended.
  for (let i = 1; i < deals.length; i++) {
    if (matchStatus({ ...next, closed: false, deals: deals.slice(0, i) }).phase !== 'inProgress') {
      throw new RuleError('These rules would have ended the match before deals already recorded.');
    }
  }
  return next;
}

// ---------------------------------------------------------------------------
// Fair randomness (Web Crypto; available in browsers, Workers and Node 20+)
// ---------------------------------------------------------------------------

export type RandomInt = (maxExclusive: number) => number;

/** Uniform integer in [0, maxExclusive) using rejection sampling to avoid modulo bias. */
export const cryptoRandomInt: RandomInt = (maxExclusive) => {
  if (!Number.isInteger(maxExclusive) || maxExclusive <= 0 || maxExclusive > 2 ** 32) {
    throw new RangeError('maxExclusive must be an integer in 1..2^32');
  }
  const limit = Math.floor(2 ** 32 / maxExclusive) * maxExclusive;
  const buf = new Uint32Array(1);
  for (;;) {
    crypto.getRandomValues(buf);
    if (buf[0]! < limit) return buf[0]! % maxExclusive;
  }
};

export function pickRandomSeat(rand: RandomInt = cryptoRandomInt): Seat {
  return rand(4) as Seat;
}

/** Fisher–Yates shuffle returning a new array. */
export function shuffle<T>(items: readonly T[], rand: RandomInt = cryptoRandomInt): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = rand(i + 1);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/** A deal as saved by versions that recorded trick counts instead of Saved/Guaa. */
export type StoredDeal = Omit<DealRecord, 'result'> & {
  result?: DealResult;
  winnerTeam?: TeamId;
  courtClaimed?: boolean;
};

/**
 * Converts a match saved before Guaa scoring: upgrades its rules and re-scores the
 * recorded deals as Guaa. The Rang team comes from each recorded dealer and the
 * result from the recorded tricks. Returns `null` when the rules are already current.
 */
export function upgradeLegacyMatch(
  rawRules: StoredRules,
  firstDealer: Seat | null,
  deals: readonly StoredDeal[],
): { rules: HouseRules; deals: DealRecord[] } | null {
  if (!isLegacyRules(rawRules)) return null;
  let rules = fitRulesToDeals(upgradeRules(rawRules), deals.length);
  const converted = deals.map((d): DealRecord => {
    const caller = trumpCallerSeat(d.dealerSeat, rules);
    const trumpTeam = d.trumpTeam ?? (caller === null ? null : teamOfSeat(caller));
    const a = d.teamATricks ?? 0;
    const b = d.teamBTricks ?? 0;
    const winner: TeamId = d.winnerTeam ?? (a > b ? 'A' : 'B');
    const result: DealResult = trumpTeam === winner ? 'saved' : 'guaa';
    const swept = Math.max(a, b) === 13 || Boolean(d.courtClaimed);
    return { ...d, trumpTeam, result, isCourt: rules.court.enabled && result === 'guaa' && swept, guaaA: 0, guaaB: 0 };
  });
  try {
    return { rules, deals: [...rescore({ rules, firstDealer, deals: converted }, rules).deals] };
  } catch {
    // Only "would have ended earlier" can fail here; keep every recorded deal playable.
    rules = fitRulesToDeals({ ...rules, endEarlyWhenDecided: false }, converted.length);
    return { rules, deals: [...rescore({ rules, firstDealer, deals: converted }, rules).deals] };
  }
}

// ---------------------------------------------------------------------------
// Per-player breakdown
// ---------------------------------------------------------------------------

/**
 * The player who chose Rang in a recorded deal, derived from its dealer and the
 * trump-caller rule. `null` when Rang went to the highest bidder (only the team
 * is known) or the derived player isn't on the recorded Rang team.
 */
export function rangPlayerOfDeal(deal: DealRecord, rules: HouseRules): Seat | null {
  const seat = trumpCallerSeat(deal.dealerSeat, rules);
  if (seat === null || (deal.trumpTeam !== null && teamOfSeat(seat) !== deal.trumpTeam)) return null;
  return seat;
}

export interface PlayerStats {
  /** Deals in which this player chose Rang. */
  called: number;
  saved: number;
  /** Guaa their team got in deals where this player chose Rang. */
  guaa: number;
  courts: number;
}

export function playerStats(state: MatchState): Record<Seat, PlayerStats> {
  const out = {} as Record<Seat, PlayerStats>;
  for (const s of ALL_SEATS) out[s] = { called: 0, saved: 0, guaa: 0, courts: 0 };
  for (const d of state.deals) {
    const seat = rangPlayerOfDeal(d, state.rules);
    if (seat === null) continue;
    const p = out[seat];
    p.called++;
    if (d.result === 'saved') p.saved++;
    p.guaa += d.guaaA + d.guaaB;
    if (d.isCourt) p.courts++;
  }
  return out;
}
